import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type {
  Account,
  BankAggregationRule,
  BankReconciliationSummary,
  MatchGroup,
  PageResponse,
  RecordProvenance,
  ReconciliationRun,
  WorkflowStatus,
} from "@/api/types";
import { useAuth } from "@/app/auth";
import { useWorkspace } from "@/app/workspace";
import { useConfirmDialog } from "@/components/ConfirmDialog";
import type { ImportWorkspaceConfig } from "@/components/ImportWorkspace";
import { money } from "@/components/format";
import { MatchSpine } from "@/components/MatchSpine";
import { Empty, PanelCollapseButton } from "@/components/Page";
import { Pagination } from "@/components/Pagination";
import { useToast } from "@/components/Toast";

type BankOption = { id: string; name: string };
type BankAggregationMode = BankAggregationRule["aggregationMode"];

export function Provenance({ value }: { value?: RecordProvenance }) {
  if (!value) return <small>Origen no disponible</small>;
  const sourceName = value.sourceDefinition?.name ?? "Carga importada";
  const accountName = value.account?.name;
  return (
    <small className="record-origin">
      <strong>{sourceName}</strong>
      <span>
        {value.sourceFile?.originalName ?? "Archivo"}
        {accountName ? ` · ${accountName}` : ""}
      </span>
    </small>
  );
}

export function ValidationReadiness({
  config,
  workflow,
  actions,
  toolbar,
}: {
  config: ImportWorkspaceConfig;
  workflow?: WorkflowStatus;
  actions?: ReactNode;
  toolbar?: ReactNode;
}) {
  if (!workflow) return null;
  const visibleBlockers = config.domain === "BANK"
    ? [...new Set(workflow.accountReadiness?.flatMap((account) => account.periodIssues ?? []) ?? [])]
    : workflow.blockers;
  const isCard = config.domain === "CARD";
  const summary = (
    <section
      className="source-readiness"
      aria-label="Disponibilidad de fuentes"
    >
      <div className={workflow.originCount ? "is-ready" : "is-missing"}>
        <small>{isCard ? (actions ? "Liquidaciones" : "Liquidaciones confirmadas") : (actions ? "Extractos" : "Extractos confirmados")}</small>
        <strong>{workflow.originCount}</strong>
      </div>
      <div className={workflow.counterpartCount ? "is-ready" : "is-missing"}>
        <small>{isCard ? (actions ? "Libro Mayor" : "Libros Mayores confirmados") : (actions ? "Libros" : "Libros Mayores confirmados")}</small>
        <strong>{workflow.counterpartCount}</strong>
      </div>
      <div>
        <small>{actions ? "Cargas" : "Cargas del período"}</small>
        <strong>{workflow.sources.length}</strong>
      </div>
      <div>
        <small>{actions ? "Listo" : "Listos para conciliar"}</small>
        <strong>{workflow.canReconcile ? "Sí" : "No"}</strong>
      </div>
    </section>
  );
  return (
    <>
      {actions || toolbar ? (
        <div className="validation-readiness-toolbar">
          {toolbar ?? summary}
          {actions && <div className="validation-readiness-actions">{actions}</div>}
        </div>
      ) : summary}
      {workflow.previewBatches.length > 0 && (
        <div className="preview-queue">
          <div>
            <strong>
              {workflow.previewBatches.length}{" "}
              {workflow.previewBatches.length === 1
                ? "carga requiere"
                : "cargas requieren"}{" "}
              confirmación
            </strong>
            <span>
              Revisá la vista previa antes de incorporar sus registros.
            </span>
          </div>
          {workflow.previewBatches.map((batch) => (
            <Link
              key={batch.id}
              className="button button--small"
              to={`${config.basePath}/cargas/${batch.id}`}
            >
              {batch.sourceDefinition.name}
            </Link>
          ))}
        </div>
      )}
      {!workflow.canReconcile && visibleBlockers.length > 0 && (
        <div className="message message--warning">
          {visibleBlockers.join(" ")}
        </div>
      )}
    </>
  );
}
function ReviewPanel({
  title,
  meta,
  children,
}: PropsWithChildren<{ title: string; meta?: ReactNode }>) {
  const [collapsed, setCollapsed] = useState(false);
  const headingId = useId();
  const contentId = useId();
  return (
    <section className="review-panel" aria-labelledby={headingId}>
      <header className="review-panel__summary">
        <span className="review-panel__heading">
          <h2 id={headingId}>{title}</h2>
          {meta ? <small>{meta}</small> : null}
        </span>
        <PanelCollapseButton
          title={title}
          expanded={!collapsed}
          controls={contentId}
          className="review-panel__toggle"
          onToggle={() => setCollapsed((value) => !value)}
        />
      </header>
      <div id={contentId} className="review-panel__body" hidden={collapsed}>
        {children}
      </div>
    </section>
  );
}

const newAggregationRule = (): Partial<BankAggregationRule> => ({
  priority: 100,
  active: true,
  aggregationMode: "BANK_MANY_TO_LEDGER_ONE",
  ledgerDescriptionPatterns: [],
  movementPatterns: [],
});

const aggregationModeLabel = (mode?: BankAggregationMode) =>
  mode === "BANK_ONE_TO_LEDGER_MANY"
    ? "1 extracto → varios asientos"
    : mode === "BANK_MANY_TO_LEDGER_MANY"
      ? "Varios extractos ↔ varios asientos"
    : "Varios extractos → 1 asiento";
const AGGREGATION_RULES_PAGE_SIZE = 5;
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" />
  </svg>
);

function BankAggregationRules({
  readOnly,
  onCreate,
  onEdit,
}: {
  readOnly: boolean;
  onCreate(event: React.MouseEvent<HTMLButtonElement>): void;
  onEdit(
    rule: BankAggregationRule,
    event: React.MouseEvent<HTMLButtonElement>,
  ): void;
}) {
  const confirmDialog = useConfirmDialog();
  const queryClient = useQueryClient();
  const toast = useToast();
  const rules = useQuery({
    queryKey: ["bank-aggregation-rules"],
    queryFn: (): Promise<BankAggregationRule[]> => responseData(apiClient.GET("/api/v1/banking/aggregation-rules")),
  });
  const remove = useMutation({
    mutationFn: (id: string) => responseData(apiClient.DELETE("/api/v1/banking/aggregation-rules/{id}", {
      params: { path: { id } },
    })),
    onSuccess: async () => {
      toast.success("Regla de agrupación eliminada.");
      await queryClient.invalidateQueries({ queryKey: ["bank-aggregation-rules"] });
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo eliminar la regla de agrupación." }),
  });
  const confirmRemoval = async (rule: BankAggregationRule) => {
    const accepted = await confirmDialog({
      title: "Eliminar regla de agrupación",
      description: `Se eliminará “${rule.name}” para ${rule.bankName}. Dejará de aplicarse en nuevas conciliaciones; los resultados ya generados no cambiarán. Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar regla",
      tone: "danger",
    });
    if (accepted) remove.mutate(rule.id);
  };
  const count = rules.data?.length ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil(count / AGGREGATION_RULES_PAGE_SIZE),
  );
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);
  const visibleRules = (rules.data ?? []).slice(
    (page - 1) * AGGREGATION_RULES_PAGE_SIZE,
    page * AGGREGATION_RULES_PAGE_SIZE,
  );
  const pageMeta = {
    page,
    pageSize: AGGREGATION_RULES_PAGE_SIZE,
    total: count,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
  return (
    <div className="aggregation-rules">
      <div className="aggregation-rules__intro">
        <div>
          <p>Las reglas son globales por banco y reconocen distintas descripciones válidas en todos los hoteles.</p>
          <small>
            {readOnly
              ? "Podés consultar los criterios y el estado de cada regla."
              : "Los cambios se aplican al ejecutar una nueva conciliación."}
          </small>
        </div>
        {!readOnly && (
          <button
            type="button"
            className="button button--primary button--small"
            onClick={onCreate}
          >
            Nueva regla
          </button>
        )}
      </div>
      <div className="aggregation-rules__heading">
        <span>Reglas configuradas</span>
        <strong aria-live="polite">{count}</strong>
      </div>
      {rules.error ? (
        <div className="message message--error aggregation-rules__error">
          <span>No se pudieron cargar las reglas de agrupación.</span>
          <button
            type="button"
            className="button button--small"
            onClick={() => void rules.refetch()}
          >
            Reintentar
          </button>
        </div>
      ) : rules.data?.length ? (
        <>
          <div className="aggregation-rules__table-wrap">
            <table className="aggregation-rules__table">
              <thead>
                <tr>
                  <th>Regla</th>
                  <th>Modalidad</th>
                  <th className="aggregation-rules__status-column">Estado</th>
                  {!readOnly && (
                    <th className="aggregation-rules__actions-column">
                      <span className="sr-only">Acciones</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {visibleRules.map((rule) => (
                    <tr
                      key={rule.id}
                      className={rule.active ? "" : "aggregation-rule-row--draft"}
                    >
                      <td>
                        <strong>{rule.name}</strong>
                        <small>{rule.bankName}</small>
                      </td>
                      <td>
                        <span className="aggregation-rule-mode">
                          {aggregationModeLabel(rule.aggregationMode)}
                        </span>
                      </td>
                      <td>
                        <em
                          className={
                            rule.active
                              ? "aggregation-rule__status"
                              : "aggregation-rule__status aggregation-rule__status--draft"
                          }
                        >
                          {rule.active ? "Activa" : "Borrador"}
                        </em>
                      </td>
                      {!readOnly && (
                        <td>
                          <div className="table-icon-actions">
                            <button
                              type="button"
                              className="button button--ghost button--small"
                              onClick={(event) => onEdit(rule, event)}
                              aria-label={`Editar ${rule.name}`}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="table-icon-button table-icon-button--danger"
                              onClick={() => void confirmRemoval(rule)}
                              aria-label={`Eliminar ${rule.name}`}
                              title={`Eliminar ${rule.name}`}
                              disabled={remove.isPending && remove.variables === rule.id}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="aggregation-rules__pagination">
            <Pagination meta={pageMeta} onPageChange={setPage} />
          </div>
        </>
      ) : (
        <div className="aggregation-rules__empty">
          <Empty>
            {rules.isLoading
              ? "Cargando reglas de agrupación…"
              : "Todavía no hay reglas de agrupación configuradas."}
          </Empty>
        </div>
      )}
    </div>
  );
}

function AggregationPatternEditor({
  legend,
  inputLabel,
  placeholder,
  help,
  values,
  onChange,
}: {
  legend: string;
  inputLabel: string;
  placeholder: string;
  help: string;
  values: string[];
  onChange(values: string[]): void;
}) {
  const [input, setInput] = useState("");
  const addPattern = () => {
    const value = input.trim().replace(/\s+/g, " ");
    if (!value) return;
    const exists = values.some(
      (pattern) =>
        pattern.trim().replace(/\s+/g, " ").toLocaleLowerCase() ===
        value.toLocaleLowerCase(),
    );
    if (!exists) onChange([...values, value]);
    setInput("");
  };
  return (
    <fieldset className="aggregation-pattern-editor aggregation-rule-form__wide">
      <legend>{legend}</legend>
      <div className="aggregation-pattern-editor__entry">
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addPattern();
            }
          }}
          placeholder={placeholder}
          aria-label={inputLabel}
        />
        <button
          type="button"
          className="button button--small"
          onClick={addPattern}
        >
          Agregar texto
        </button>
      </div>
      <small>{help}</small>
      <div className="aggregation-pattern-editor__items" aria-live="polite">
        {values.map((pattern) => (
          <span key={pattern}>
            <b>{pattern}</b>
            <button
              type="button"
              onClick={() => onChange(values.filter((value) => value !== pattern))}
              aria-label={`Quitar ${pattern}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </fieldset>
  );
}

function SingleAggregationPatternInput({
  legend,
  inputLabel,
  placeholder,
  help,
  value,
  required,
  onChange,
}: {
  legend: string;
  inputLabel: string;
  placeholder: string;
  help: string;
  value: string;
  required: boolean;
  onChange(value: string): void;
}) {
  return (
    <fieldset className="aggregation-pattern-editor aggregation-pattern-editor--single aggregation-rule-form__wide">
      <legend>{legend}</legend>
      <div className="aggregation-pattern-editor__single-entry">
        <input
          required={required}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={inputLabel}
        />
        <span aria-hidden="true">1 único</span>
      </div>
      <small>{help}</small>
    </fieldset>
  );
}

function BankAggregationRuleEditor({
  rule,
  onClose,
  onSaved,
}: {
  rule: Partial<BankAggregationRule>;
  onClose(): void;
  onSaved(): void;
}) {
  const queryClient = useQueryClient();
  const confirmDialog = useConfirmDialog();
  const toast = useToast();
  const [draft, setDraft] = useState<Partial<BankAggregationRule>>(() => ({
    ...rule,
    aggregationMode: rule.aggregationMode ?? "BANK_MANY_TO_LEDGER_ONE",
    ledgerDescriptionPatterns: rule.ledgerDescriptionPatterns?.length
      ? rule.ledgerDescriptionPatterns
      : rule.ledgerDescriptionPattern
        ? [rule.ledgerDescriptionPattern]
        : [],
    movementPatterns: rule.movementPatterns ?? [],
  }));
  const nameInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    nameInput.current?.focus();
  }, []);
  const banks = useQuery({
    queryKey: ["bank-masters"],
    queryFn: async (): Promise<BankOption[]> => (await responseData(apiClient.GET("/api/v1/banking/banks", { params: { query: { pageSize: 100, active: "true" } } }))).items,
  });
  const save = useMutation({
    mutationFn: () => {
      const payload = draft;
      const selectedBankId = payload.bankId || banks.data?.[0]?.id;
      if (!selectedBankId) throw new Error("Seleccioná un banco para la regla.");
      if (!payload.name?.trim()) throw new Error("Ingresá un nombre para la regla.");
      return responseData(apiClient.POST("/api/v1/banking/aggregation-rules", {
        body: {
          id: payload.id,
          bankId: selectedBankId,
          code: payload.code,
          name: payload.name,
          aggregationMode:
            payload.aggregationMode ?? "BANK_MANY_TO_LEDGER_ONE",
          ledgerDescriptionPattern: payload.ledgerDescriptionPattern,
          ledgerDescriptionPatterns:
            payload.ledgerDescriptionPatterns ?? [],
          movementPatterns: payload.movementPatterns ?? [],
          priority: payload.priority,
          active: payload.active,
        },
      }));
    },
    onSuccess: async () => {
      toast.success("Regla de agrupación guardada.");
      await queryClient.invalidateQueries({
        queryKey: ["bank-aggregation-rules"],
      });
      onSaved();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo guardar la regla." }),
  });
  const update = <K extends keyof BankAggregationRule>(
    key: K,
    value: BankAggregationRule[K],
  ) => setDraft((current) => ({ ...current, [key]: value }));
  const changeAggregationMode = async (mode: BankAggregationMode) => {
    if (draft.aggregationMode === mode) return;
    if (mode === "BANK_MANY_TO_LEDGER_MANY") {
      setDraft((current) => ({ ...current, aggregationMode: mode }));
      return;
    }
    const uniqueSide =
      mode === "BANK_ONE_TO_LEDGER_MANY"
        ? {
            key: "movementPatterns" as const,
            label: "El extracto",
            values: draft.movementPatterns ?? [],
          }
        : {
            key: "ledgerDescriptionPatterns" as const,
            label: "El Libro Mayor",
            values: draft.ledgerDescriptionPatterns ?? [],
          };
    if (uniqueSide.values.length > 1) {
      const removedCount = uniqueSide.values.length - 1;
      const confirmed = await confirmDialog({
        title: "Cambiar modalidad",
        description: `${uniqueSide.label} pasará a admitir un solo texto. Se conservará “${uniqueSide.values[0]}” y se eliminará${removedCount === 1 ? "" : "n"} ${removedCount === 1 ? "el texto restante" : `los ${removedCount} textos restantes`}.`,
        confirmLabel: "Cambiar y conservar uno",
        cancelLabel: "Mantener modalidad",
      });
      if (!confirmed) return;
    }
    setDraft((current) => ({
      ...current,
      aggregationMode: mode,
      [uniqueSide.key]: uniqueSide.values.slice(0, 1),
    }));
  };
  const inverseMode = draft.aggregationMode === "BANK_ONE_TO_LEDGER_MANY";
  const manyToManyMode = draft.aggregationMode === "BANK_MANY_TO_LEDGER_MANY";
  const active = draft.active ?? true;
  const hasMovementPattern = draft.movementPatterns?.some((value) => value.trim());
  const hasLedgerPattern = draft.ledgerDescriptionPatterns?.some((value) => value.trim());
  const activeRuleIncomplete =
    active && (!hasMovementPattern || !hasLedgerPattern);
  return (
    <aside
      className="aggregation-rule-editor"
      aria-labelledby="aggregation-rule-editor-title"
    >
      <header>
        <div>
          <small>{draft.id ? "Editar criterio" : "Nuevo criterio"}</small>
          <h3 id="aggregation-rule-editor-title">
            {draft.id ? draft.name || "Regla de agrupación" : "Nueva regla"}
          </h3>
        </div>
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Cerrar editor"
        >
          ×
        </button>
      </header>
      <form
        className="aggregation-rule-form"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <label>
          <span>Banco</span>
          <select
            required
            value={draft.bankId ?? banks.data?.[0]?.id ?? ""}
            onChange={(event) => update("bankId", event.target.value)}
          >
            <option value="">Seleccionar banco</option>
            {banks.data?.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Nombre de la regla</span>
          <input
            ref={nameInput}
            required
            value={draft.name ?? ""}
            onChange={(event) => update("name", event.target.value)}
          />
        </label>
        <fieldset className="aggregation-mode-selector aggregation-rule-form__wide">
          <legend>Cómo se agrupa</legend>
          <div>
            <label
              className={
                draft.aggregationMode === "BANK_MANY_TO_LEDGER_ONE" || !draft.aggregationMode
                  ? "is-selected"
                  : ""
              }
            >
              <input
                type="radio"
                name="aggregationMode"
                value="BANK_MANY_TO_LEDGER_ONE"
                checked={
                  draft.aggregationMode === "BANK_MANY_TO_LEDGER_ONE" || !draft.aggregationMode
                }
                onChange={() =>
                  void changeAggregationMode("BANK_MANY_TO_LEDGER_ONE")
                }
              />
              <span>
                <b>N extractos</b>
                <i aria-hidden="true">→</i>
                <b>1 asiento</b>
              </span>
              <small>Sumar movimientos bancarios contra un asiento.</small>
            </label>
            <label
              className={
                draft.aggregationMode === "BANK_ONE_TO_LEDGER_MANY"
                  ? "is-selected"
                  : ""
              }
            >
              <input
                type="radio"
                name="aggregationMode"
                value="BANK_ONE_TO_LEDGER_MANY"
                checked={
                  draft.aggregationMode === "BANK_ONE_TO_LEDGER_MANY"
                }
                onChange={() =>
                  void changeAggregationMode("BANK_ONE_TO_LEDGER_MANY")
                }
              />
              <span>
                <b>1 extracto</b>
                <i aria-hidden="true">→</i>
                <b>N asientos</b>
              </span>
              <small>Sumar debe y haber contra un movimiento bancario.</small>
            </label>
            <label
              className={manyToManyMode ? "is-selected" : ""}
            >
              <input
                type="radio"
                name="aggregationMode"
                value="BANK_MANY_TO_LEDGER_MANY"
                checked={manyToManyMode}
                onChange={() =>
                  void changeAggregationMode("BANK_MANY_TO_LEDGER_MANY")
                }
              />
              <span>
                <b>N extractos</b>
                <i aria-hidden="true">↔</i>
                <b>N asientos</b>
              </span>
              <small>Comparar el total de varios movimientos y asientos.</small>
            </label>
          </div>
        </fieldset>
        <p className="aggregation-rule-form__equation aggregation-rule-form__wide">
          {manyToManyMode
            ? "Los movimientos y asientos encontrados se suman por separado. Sólo el redondeo histórico N↔N puede conciliarse automáticamente; cualquier otra diferencia requiere aprobación según el tope vigente."
            : inverseMode
            ? "Debe existir un movimiento del extracto. Los asientos encontrados se suman como Debe − Haber."
            : "Debe existir un asiento del Libro Mayor. Los movimientos encontrados se suman como Crédito − Débito."}
        </p>
        {inverseMode ? (
          <SingleAggregationPatternInput
            legend="Movimiento único del extracto"
            inputLabel="Texto único de movimiento bancario"
            placeholder="Ej. AERO SRL"
            help="Este texto debe identificar exactamente un movimiento bancario."
            value={draft.movementPatterns?.[0] ?? ""}
            required={active}
            onChange={(value) =>
              update("movementPatterns", value ? [value] : [])
            }
          />
        ) : (
          <AggregationPatternEditor
            legend="Movimientos del extracto que se agrupan"
            inputLabel="Nuevo texto de movimiento bancario"
            placeholder="Ej. AERO SRL"
            help="Agregá uno o más textos alternativos para encontrar los movimientos que se sumarán."
            values={draft.movementPatterns ?? []}
            onChange={(values) => update("movementPatterns", values)}
          />
        )}
        {inverseMode || manyToManyMode ? (
          <AggregationPatternEditor
            legend="Asientos del Libro Mayor que se agrupan"
            inputLabel="Nuevo texto de asiento del Libro Mayor"
            placeholder="Ej. RET GANANCIAS AERO SRL"
            help="Agregá uno o más textos. El motor sumará cada asiento respetando Debe/Haber."
            values={draft.ledgerDescriptionPatterns ?? []}
            onChange={(values) => update("ledgerDescriptionPatterns", values)}
          />
        ) : (
          <SingleAggregationPatternInput
            legend="Asiento único del Libro Mayor"
            inputLabel="Texto único de asiento del Libro Mayor"
            placeholder="Ej. SUSCRIPCIÓN FIMA"
            help="Este texto debe identificar exactamente un asiento del Libro Mayor."
            value={draft.ledgerDescriptionPatterns?.[0] ?? ""}
            required={active}
            onChange={(value) =>
              update("ledgerDescriptionPatterns", value ? [value] : [])
            }
          />
        )}
        <label className="check-field aggregation-rule-form__wide">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => update("active", event.target.checked)}
          />{" "}
          Regla activa
        </label>
        <footer className="aggregation-rule-form__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            className="button button--primary"
            disabled={save.isPending || activeRuleIncomplete}
            title={
              activeRuleIncomplete
                ? "Agregá al menos un texto del extracto y uno del Libro Mayor."
                : undefined
            }
          >
            {draft.id ? "Actualizar regla" : "Guardar regla"}
          </button>
        </footer>
      </form>
    </aside>
  );
}
export function BankAggregationRulesModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose(): void;
}) {
  const { period } = useWorkspace();
  const { user } = useAuth();
  const closeButton = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const editorTrigger = useRef<HTMLElement | null>(null);
  const [editingRule, setEditingRule] =
    useState<Partial<BankAggregationRule> | null>(null);
  const closeEditor = useCallback(() => {
    setEditingRule(null);
    window.setTimeout(() => editorTrigger.current?.focus(), 0);
  }, []);
  useEffect(() => {
    if (!open) {
      setEditingRule(null);
      return;
    }
    returnFocus.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    closeButton.current?.focus();
    return () => {
      returnFocus.current?.focus();
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") editingRule ? closeEditor() : onClose();
    };
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("keydown", escape);
    };
  }, [open, onClose, editingRule, closeEditor]);
  if (!open) return null;
  const readOnly = !["SUPERVISOR", "ADMIN"].includes(user?.role ?? "");
  const openEditor = (
    rule: Partial<BankAggregationRule>,
    trigger: HTMLElement,
  ) => {
    editorTrigger.current = trigger;
    setEditingRule(rule);
  };
  return (
    <div
      className="modal-backdrop aggregation-rules-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`aggregation-rules-workspace ${editingRule ? "aggregation-rules-workspace--editing" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="aggregation-rules-title"
      >
        <div className="import-modal aggregation-rules-modal">
          <header>
            <div>
              <small>Configuración global por banco · {period}</small>
              <h2 id="aggregation-rules-title">
                Reglas de agrupación bancaria
              </h2>
            </div>
            <button
              ref={closeButton}
              type="button"
              className="modal-close"
              onClick={onClose}
              aria-label="Cerrar modal"
            >
              ×
            </button>
          </header>
          <BankAggregationRules
            readOnly={readOnly}
            onCreate={(event) =>
              openEditor(newAggregationRule(), event.currentTarget)
            }
            onEdit={(rule, event) => openEditor(rule, event.currentTarget)}
          />
        </div>
        {editingRule && (
          <BankAggregationRuleEditor
            key={editingRule.id ?? "new"}
            rule={editingRule}
            onClose={closeEditor}
            onSaved={closeEditor}
          />
        )}
      </section>
    </div>
  );
}
export function ReviewWorkspace({
  config,
  workflow,
  run,
  hiddenEvidenceNames,
}: {
  config: ImportWorkspaceConfig;
  workflow?: WorkflowStatus;
  run?: ReconciliationRun | null;
  hiddenEvidenceNames?: readonly string[];
}) {
  const toast = useToast();
  const { user } = useAuth();
  const { hotelId, period } = useWorkspace();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const reconciledPage = Number(params.get("reviewAutomaticPage") ?? 1);
  const criteriaPage = Number(params.get("reviewCriteriaPage") ?? 1);
  const masterId = params.get("masterId") ?? "";
  const bankId = params.get("bankId") ?? (config.domain === "BANK" ? masterId : "");
  const currency = params.get("currency") ?? "";
  const scopeParams = new URLSearchParams();
  if (config.domain === "BANK") {
    if (bankId) scopeParams.set("bankId", bankId);
    if (currency) scopeParams.set("currency", currency);
  } else if (masterId) scopeParams.set("masterId", masterId);
  const masterParam = scopeParams.toString() ? `&${scopeParams}` : "";
  const criteriaStatuses =
    "SUGGESTED,UNLINKED,REVIEW,UNMATCHED";
  const reconciledGroups = useQuery({
    queryKey: [
      "review-groups",
      "reconciled",
      config.domain,
      hotelId,
      period,
      reconciledPage,
      masterId,
      bankId,
      currency,
    ],
    queryFn: (): Promise<PageResponse<MatchGroup>> => config.domain === "BANK"
      ? responseData(apiClient.GET("/api/v1/banking/review/groups", { params: { query: { hotelId, period, page: String(reconciledPage), pageSize: "20", status: "AUTO,APPROVED,ADJUSTED", bankId: bankId || undefined, currency: currency === "ARS" || currency === "USD" ? currency : undefined } } }))
      : responseData(apiClient.GET("/api/v1/cards/review/groups", { params: { query: { hotelId, period, page: String(reconciledPage), pageSize: "20", status: "AUTO,APPROVED,ADJUSTED", cardBrandId: masterId || undefined } } })),
    enabled: Boolean(hotelId),
  });
  const criteriaGroups = useQuery({
    queryKey: [
      "review-groups",
      "criteria",
      config.domain,
      hotelId,
      period,
      criteriaPage,
      masterId,
      bankId,
      currency,
    ],
    queryFn: (): Promise<PageResponse<MatchGroup>> => config.domain === "BANK"
      ? responseData(apiClient.GET("/api/v1/banking/review/groups", { params: { query: { hotelId, period, page: String(criteriaPage), pageSize: "20", status: criteriaStatuses, bankId: bankId || undefined, currency: currency === "ARS" || currency === "USD" ? currency : undefined } } }))
      : responseData(apiClient.GET("/api/v1/cards/review/groups", { params: { query: { hotelId, period, page: String(criteriaPage), pageSize: "20", status: criteriaStatuses, cardBrandId: masterId || undefined } } })),
    enabled: Boolean(hotelId),
  });
  const bankSummary = useQuery({
    queryKey: ["bank-review-summary", hotelId, period, bankId, currency],
    queryFn: (): Promise<BankReconciliationSummary> => responseData(apiClient.GET("/api/v1/banking/review/summary", { params: { query: { hotelId, period, bankId: bankId || undefined, currency: currency === "ARS" || currency === "USD" ? currency : undefined } } })),
    enabled: config.domain === "BANK" && Boolean(hotelId),
  });
  const [selectedCriteriaIds, setSelectedCriteriaIds] = useState<Set<string>>(
    new Set(),
  );
  const selectAllCriteriaRef = useRef<HTMLInputElement>(null);
  const bankMaxApprovableDifference = Number(workflow?.maxApprovableDifference);
  const isBankDifferenceApprovable = (group: MatchGroup) =>
    config.domain !== "BANK" || (
      Number.isFinite(bankMaxApprovableDifference) &&
      Math.abs(Number(group.amountDifference)) <= bankMaxApprovableDifference
    );
  const selectableCriteriaIds =
    criteriaGroups.data?.items
      .filter(
        (group) =>
          group.status === "SUGGESTED" &&
          isBankDifferenceApprovable(group),
      )
      .map((group) => group.id) ?? [];
  const selectableCriteriaKey = selectableCriteriaIds.join(",");
  const allCriteriaSelected =
    selectableCriteriaIds.length > 0 &&
    selectableCriteriaIds.every((id) => selectedCriteriaIds.has(id));
  const selectedCriteriaCount = selectableCriteriaIds.filter((id) =>
    selectedCriteriaIds.has(id),
  ).length;
  useEffect(() => {
    const visibleIds = new Set(selectableCriteriaIds);
    setSelectedCriteriaIds((current) => {
      const next = new Set([...current].filter((id) => visibleIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [selectableCriteriaKey]);
  useEffect(() => {
    if (selectAllCriteriaRef.current) {
      selectAllCriteriaRef.current.indeterminate =
        selectedCriteriaCount > 0 && !allCriteriaSelected;
    }
  }, [allCriteriaSelected, selectedCriteriaCount]);
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["review-groups"],
      }),
      queryClient.invalidateQueries({ queryKey: ["workflow", config.domain] }),
      queryClient.invalidateQueries({
        queryKey: ["run", config.domain.toLowerCase()],
      }),
      queryClient.invalidateQueries({
        queryKey: ["bank-review-summary", hotelId, period],
      }),
      queryClient.invalidateQueries({
        queryKey: ["card-review-summary", hotelId, period],
      }),
      queryClient.invalidateQueries({ queryKey: ["card-brand-overview", hotelId, period] }),
    ]);
  const unlink = async (id: string, successMessage: string) => {
    try {
      if (config.domain === "BANK") await responseData(apiClient.POST("/api/v1/banking/matches/{id}/unlink", { params: { path: { id } } }));
      else await responseData(apiClient.POST("/api/v1/cards/matches/{id}/unlink", { params: { path: { id } } }));
      toast.success(successMessage);
      await refresh();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason : new Error("No se pudo completar la operación."));
    }
  };
  const demoteCard = async (group: MatchGroup) => {
    try {
      await responseData(apiClient.POST("/api/v1/cards/matches/{id}/demote", { params: { path: { id: group.id } } }));
      toast.success(group.status === "APPROVED" ? "La aprobación se deshizo y la coincidencia volvió a criterio." : "La conciliación automática pasó a criterio.");
      await refresh();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason : new Error("No se pudo deshacer la conciliación."));
    }
  };
  const cardUndo = (group: MatchGroup) => {
    const manualGroup = group.method === "MANUAL" || (group.ruleCode ?? "").toLowerCase() === "manual";
    if (group.status === "AUTO") return () => void demoteCard(group);
    if (group.status === "APPROVED" && manualGroup && (user?.role === "ADMIN" || user?.role === "SUPERVISOR")) return () => void unlink(group.id, "Conciliación manual deshecha. Los registros volvieron a pendientes.");
    if (group.status === "APPROVED" && !manualGroup) return () => void demoteCard(group);
    if (group.status === "SUGGESTED" && manualGroup) return () => void unlink(group.id, "Conciliación manual deshecha. Los registros volvieron a pendientes.");
    return undefined;
  };
  const approve = useMutation({
    mutationFn: ({
      ids,
      bulk = false,
    }: {
      ids: string[];
      bulk?: boolean;
    }) => {
      const id = ids[0] ?? "";
      if (config.domain === "BANK") return bulk
        ? responseData(apiClient.POST("/api/v1/banking/matches/approve", { body: { ids } })).then(() => undefined)
        : responseData(apiClient.POST("/api/v1/banking/matches/{id}/approve", { params: { path: { id } } })).then(() => undefined);
      return bulk
        ? responseData(apiClient.POST("/api/v1/cards/matches/approve", { body: { ids } })).then(() => undefined)
        : responseData(apiClient.POST("/api/v1/cards/matches/{id}/approve", { params: { path: { id } } })).then(() => undefined);
    },
    onSuccess: async (_result, request) => {
      toast.success(request.bulk ? `${request.ids.length} coincidencias aprobadas.` : "Coincidencia aprobada.");
      setSelectedCriteriaIds((current) => {
        const next = new Set(current);
        request.ids.forEach((id) => next.delete(id));
        return next;
      });
      await refresh();
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo aprobar la coincidencia." }),
  });
  const toggleCriteriaSelection = (groupId: string) => {
    setSelectedCriteriaIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };
  const toggleAllCriteria = () => {
    setSelectedCriteriaIds(
      allCriteriaSelected ? new Set() : new Set(selectableCriteriaIds),
    );
  };
  const setPage = (key: string, value: number) => {
    const next = new URLSearchParams(params);
    next.set(key, String(value));
    setParams(next);
  };
  const summaryOrder = [
    "auto",
    "approved",
    "adjusted",
    "suggested",
    "unmatchedOrigin",
    "unmatchedCounterpart",
  ];
  const storedSummaryEntries = run?.summary
    ? summaryOrder.flatMap((key) => {
        const value = run.summary[key];
        return typeof value === "number" && Number.isFinite(value)
          ? [[key, value] as const]
          : [];
      })
    : [];
  const summaryEntries = bankSummary.data?.statusCounts
    ? summaryOrder.map((key) => [
        key,
        bankSummary.data!.statusCounts![
          key as keyof NonNullable<BankReconciliationSummary["statusCounts"]>
        ],
      ] as const)
    : storedSummaryEntries;
  const observationCount = config.domain === "BANK"
    ? criteriaGroups.data?.meta.total ?? 0
    : criteriaGroups.data?.meta.total ?? workflow?.exceptionCount ?? 0;
  const summaryLabel = (label: string) => {
    const normalized = label.replace(/[\s_-]+/g, "").toLowerCase();
    const labels: Record<string, string> = {
      auto: "Autom\u00e1ticas",
      approved: "Aprobadas",
      adjusted: "Ajustadas",
      suggested: "Sugeridas",
      unmatchedbank: "Sin coincidencia en extracto",
      unmatchedledger: "Sin coincidencia en libro mayor",
      unmatchedorigin: config.domain === "CARD" ? "Cupones sin coincidencia" : "Sin coincidencia · origen",
      unmatchedcounterpart: config.domain === "CARD" ? "Asientos sin coincidencia" : "Sin coincidencia · contraparte",
      unmatchedsettlements: "Liquidaciones sin coincidencia",
      unmatched: "Sin coincidencia",
    };
    return labels[normalized] ?? label.replace(/([A-Z])/g, " $1");
  };
  return (
    <>
      <div className="review-heading">
        <div className="review-heading__copy">
          <small>Control final · {period}</small>
          <h2>{observationCount} observaciones para revisar</h2>
        </div>
        {summaryEntries.length > 0 && (
          <section
            className="run-summary"
            aria-label="Resumen de conciliaci\u00f3n"
          >
            {summaryEntries.map(([label, value]) => (
              <div key={label}>
                <small>{summaryLabel(label)}</small>
                <strong>{value}</strong>
              </div>
            ))}
          </section>
        )}
      </div>
      {config.domain === "BANK" && bankSummary.data && (
        <section
          className="bank-reconciliation-summary"
          aria-label="Totales bancarios conciliados"
        >
          {(bankSummary.data.currencies?.length ? bankSummary.data.currencies : [{ currency: bankSummary.data.currency ?? "ARS", groupCount: bankSummary.data.groupCount, totals: bankSummary.data.totals }]).map((currencySummary) => <div key={currencySummary.currency}>
            <article>
              <small>Extracto · Débito neto</small><em>{currencySummary.currency}</em>
              <strong className="mono">
                {money(currencySummary.totals.bank.debit, currencySummary.currency)}
              </strong>
            </article>
            <article>
              <small>Extracto · Crédito neto</small><em>{currencySummary.currency}</em>
              <strong className="mono">
                {money(currencySummary.totals.bank.credit, currencySummary.currency)}
              </strong>
            </article>
            <article>
              <small>Libro Mayor · Debe neto</small><em>{currencySummary.currency}</em>
              <strong className="mono">
                {money(currencySummary.totals.ledger.debit, currencySummary.currency)}
              </strong>
            </article>
            <article>
              <small>Libro Mayor · Haber neto</small><em>{currencySummary.currency}</em>
              <strong className="mono">
                {money(currencySummary.totals.ledger.credit, currencySummary.currency)}
              </strong>
            </article>
          </div>)}
        </section>
      )}
      <div className="review-match-sections">
        <ReviewPanel
          title="Conciliadas"
          meta={`${reconciledGroups.data?.meta.total ?? 0}`}
        >
          <div className="match-list match-list--compact">
            {reconciledGroups.data?.items.length ? (
              reconciledGroups.data.items.map((group) => (
                <MatchSpine
                  key={group.id}
                  group={group}
                  variant="compact"
                  hiddenEvidenceNames={hiddenEvidenceNames}
                  maxApprovableDifference={workflow?.maxApprovableDifference}
                  onUndo={config.domain === "CARD" ? cardUndo(group) : undefined}
                  onUnlink={config.domain === "BANK" ? () => void unlink(group.id, "Coincidencia desvinculada.") : undefined}
                />
              ))
            ) : (
              <Empty>
                No hay conciliaciones efectivas en esta corrida.
              </Empty>
            )}
          </div>
          <Pagination
            meta={reconciledGroups.data?.meta}
            onPageChange={(value) => setPage("reviewAutomaticPage", value)}
          />
        </ReviewPanel>
        <ReviewPanel
          title="Coincidencias que requieren criterio"
          meta={`${criteriaGroups.data?.meta.total ?? 0}`}
        >
          {selectableCriteriaIds.length > 0 && (
            <div className="criteria-batch-bar">
              <label className="criteria-batch-bar__selector">
                <input
                  ref={selectAllCriteriaRef}
                  type="checkbox"
                  checked={allCriteriaSelected}
                  disabled={approve.isPending}
                  onChange={toggleAllCriteria}
                />
                <span>
                  <strong>Seleccionar esta página</strong>
                  <small>
                    {selectedCriteriaCount
                      ? `${selectedCriteriaCount} de ${selectableCriteriaIds.length} seleccionadas`
                      : `${selectableCriteriaIds.length} disponibles`}
                  </small>
                </span>
              </label>
              <button
                type="button"
                className="button button--primary"
                disabled={!selectedCriteriaCount || approve.isPending}
                aria-busy={approve.isPending && Boolean(approve.variables?.bulk)}
                onClick={() =>
                  approve.mutate({
                    ids: selectableCriteriaIds.filter((id) =>
                      selectedCriteriaIds.has(id),
                    ),
                    bulk: true,
                  })
                }
              >
                {approve.isPending && approve.variables?.bulk
                  ? "Aprobando selección…"
                  : `Aprobar seleccionadas (${selectedCriteriaCount})`}
              </button>
            </div>
          )}
          <div className="match-list match-list--compact">
            {criteriaGroups.data?.items.length ? (
              criteriaGroups.data.items.map((group) => {
                const difference = Number(group.amountDifference);
                const hasBankDifference =
                  config.domain === "BANK" && difference !== 0;
                const selectable =
                  group.status === "SUGGESTED" && isBankDifferenceApprovable(group);
                const match = (
                  <MatchSpine
                    group={group}
                    variant="compact"
                    hiddenEvidenceNames={hiddenEvidenceNames}
                    maxApprovableDifference={workflow?.maxApprovableDifference}
                    showDifference
                    approveDisabled={approve.isPending}
                    approvePending={
                      approve.isPending &&
                      Boolean(approve.variables?.ids.includes(group.id))
                    }
                    onApprove={
                      selectable
                        ? () => approve.mutate({ ids: [group.id] })
                        : undefined
                    }
                    approvalBlockedReason={
                      hasBankDifference && !selectable
                        ? Number.isFinite(bankMaxApprovableDifference)
                          ? group.method === "AGGREGATION"
                            ? <>La diferencia supera el tope permitido de {money(bankMaxApprovableDifference, group.currency)}. Podés corregir los datos y volver a conciliar, editar la regla de agrupación y volver a conciliar, o seleccionar los registros correctos en <Link className="match-approval-blocked__link" to={`${config.basePath}/movimientos?status=pending${masterParam}`}>Validar movimientos</Link> y conciliarlos manualmente.</>
                            : `La diferencia supera el tope permitido de ${money(bankMaxApprovableDifference, group.currency)}. Corregí los datos y volvé a conciliar.`
                          : "No se pudo obtener el tope de diferencia permitido. Recargá la página antes de aprobar."
                        : undefined
                    }
                    onUndo={config.domain === "CARD" ? cardUndo(group) : undefined}
                    onUnlink={config.domain === "BANK" ? () => void unlink(group.id, "Coincidencia desvinculada.") : undefined}
                  />
                );
                return selectable ? (
                  <div
                    key={group.id}
                    className={`criteria-selectable-match${selectedCriteriaIds.has(group.id) ? " criteria-selectable-match--selected" : ""}`}
                  >
                    <div className="criteria-selectable-match__check">
                      <input
                        type="checkbox"
                        checked={selectedCriteriaIds.has(group.id)}
                        disabled={approve.isPending}
                        aria-label={`Seleccionar coincidencia ${group.items[0]?.label ?? group.id}`}
                        onChange={() => toggleCriteriaSelection(group.id)}
                      />
                    </div>
                    {match}
                  </div>
                ) : (
                  <div key={group.id}>{match}</div>
                );
              })
            ) : (
              <Empty>No hay coincidencias pendientes de revisión.</Empty>
            )}
          </div>
          <Pagination
            meta={criteriaGroups.data?.meta}
            onPageChange={(value) => setPage("reviewCriteriaPage", value)}
          />
        </ReviewPanel>
      </div>
      <div className="review-unmatched">
        <span>
          Los registros sin coincidencia permanecen visibles en Validar.
        </span>
        <Link
          className="button button--ghost"
          to={`${config.basePath}/${config.domain === "BANK" ? "movimientos" : config.domain === "CARD" ? "liquidaciones" : "reservas"}?status=pending${masterParam}`}
        >
          Ver pendientes
        </Link>
      </div>
    </>
  );
}
